import React, { createContext, useState, useContext, useCallback } from "react";
import { AuthState, User, AuthTokens, LoginRequest } from "../types/auth";
import {
  login as loginService,
  logout as logoutService,
} from "../services/authService";
import { setAccessToken } from "../api/api";

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: () => boolean;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);

  const isAuthenticated = !!user && !!tokens;

  const login = useCallback(async (credentials: LoginRequest) => {
    const response = await loginService(credentials);

    if (!response.portalAdmin) {
      throw new Error("You don't have access to this page.");
    }

    const newTokens: AuthTokens = {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresAtUtc: response.expiresAtUtc,
      expiresInSeconds: response.expiresInSeconds,
      refreshTokenExpiresAtUtc: response.refreshTokenExpiresAtUtc,
    };

    const newUser: User = {
      individualId: response.individualId,
      email: response.email,
      fullName: response.fullName,
    };

    setAccessToken(response.accessToken);

    setTokens(newTokens);
    setUser(newUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      if (tokens?.refreshToken) {
        await logoutService({ refreshToken: tokens.refreshToken });
      }
    } catch (error) {
      console.error("Logout API call failed, clearing session anyway:", error);
    } finally {
      setAccessToken(null);
      setTokens(null);
      setUser(null);
    }
  }, [tokens]);

  const isAdmin = useCallback(() => {
    // Will update once roles are confirmed
    return !!user;
  }, [user]);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        isAuthenticated,
        login,
        logout,
        isAdmin,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
